import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { translate } from "../../utils/Common";
import { LuChevronLeft, LuChevronRight, LuSearch, LuX, LuBoxes } from "react-icons/lu";
import { Card } from "@/components/ui/card";
import { FIELD_SKIN } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";
import ItemStatusPlatforms from "./ItemStatusPlatforms";
import ItemPackageLockers from "./ItemPackageLockers";

/**
 * Paginated locker summary.
 *
 * What the rewrite fixes, measured on the old one:
 * - Rows were 30px tall carrying 14px text, so the table read as a wall.
 * - The status filters rendered their count and label with nothing between
 *   them — literally "2ONLINE" and "65OFFLINE", which reads as a glitch.
 * - The header had a subtitle built from three chained `translate()` calls
 *   falling back to each other; none of those keys exist, so it was an empty
 *   paragraph.
 * - The counts were left-aligned in ~190px columns, so no two digits ever
 *   lined up.
 */

const PAGE_SIZE = 10;

const TablePaginationPlatforms = ({ data, headerRow, title, filterStatus, isActivePackage, notFound = null }) => {
  const history = useHistory();
  // The locker detail is local state inside the Platforms screen, not a route of
  // its own, so the row hands it the id in the query string and that screen
  // preselects the matching locker from its own data.
  const openLocker = (id, name) =>
    history.push(
      `/dashboard/platforms/general?locker=${encodeURIComponent(id ?? '')}&lockerName=${encodeURIComponent(name ?? '')}`
    );
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const normalize = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

  const filtered = useMemo(() => {
    const term = normalize(search);
    let rows = (data || []).filter((p) => normalize(p.Plataforma).includes(term));
    if (status) rows = rows.filter((p) => p.Estatus === (status === "active" ? "ONLINE" : "OFFLINE"));
    return rows;
  }, [data, search, status]);

  useEffect(() => setPage(0), [search, status, data]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const countBy = (s) => (data || []).filter((p) => p.Estatus === s).length;

  const StatusChip = ({ value, count, tone, label }) => {
    const active = status === value;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setStatus(active ? "" : value)}
        className={cn(
          "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          active ? tone.on : tone.off
        )}
      >
        <span aria-hidden="true" className={cn("size-1.5 rounded-full", tone.dot)} />
        {/* The count and the word need to be separate elements; run together
            they read as one broken token. */}
        <span className="tabular-nums">{count}</span>
        <span className="uppercase tracking-wider">{label}</span>
      </button>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-foreground">
          <LuBoxes size={16} className="text-muted-foreground" />
          {title}
          <span className="text-xs font-normal text-muted-foreground">
            {(data || []).length}
          </span>
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <LuSearch
              size={14}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`${translate("global.search")}...`}
              className={cn(FIELD_SKIN, "h-8 w-44 pl-8 pr-7 text-xs")}
            />
            {search && (
              <button
                type="button"
                aria-label={translate("global.clean")}
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <LuX size={13} />
              </button>
            )}
          </div>

          {filterStatus && !isActivePackage && (
            <div className="flex items-center gap-1.5">
              <StatusChip
                value="active"
                count={countBy("ONLINE")}
                label={translate("dashboard.home.status_platforms.status.online")}
                // Same fill as the row badge, so the filter and the thing it
                // filters read as one. Idle matches the badge, active is a step up.
                tone={{
                  on: "bg-success/28 text-success",
                  off: "bg-success/15 text-success hover:bg-success/22",
                  dot: "bg-current opacity-70",
                }}
              />
              <StatusChip
                value="inactive"
                count={countBy("OFFLINE")}
                label={translate("dashboard.home.status_platforms.status.offline")}
                tone={{
                  on: "bg-destructive/28 text-destructive",
                  off: "bg-destructive/15 text-destructive hover:bg-destructive/22",
                  dot: "bg-current opacity-70",
                }}
              />
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label={translate("global.previous") || "<"}
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent disabled:opacity-30"
              >
                <LuChevronLeft size={15} />
              </button>
              <span className="min-w-[3.5rem] text-center text-[11px] tabular-nums text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                aria-label={translate("global.next") || ">"}
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent disabled:opacity-30"
              >
                <LuChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead className="border-b border-border [&_th]:whitespace-nowrap [&_th]:px-3 [&_th]:py-2 [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-muted-foreground">
            {headerRow}
          </thead>
          <tbody>
            {!data ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={`skeleton-${i}`}>
                  <td colSpan="7" className="px-3 py-2">
                    <div className="bx-skeleton h-6 w-full" aria-hidden="true" />
                  </td>
                </tr>
              ))
            ) : visible.length > 0 ? (
              visible.map((item, i) =>
                !isActivePackage ? (
                  <ItemStatusPlatforms key={`platform-${i}`} item={item} onOpen={openLocker} />
                ) : (
                  <ItemPackageLockers key={`package-${i}`} item={item} />
                )
              )
            ) : (
              <tr>
                <td colSpan="7" className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {notFound ?? translate("dashboard.home.status_platforms.asign")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default TablePaginationPlatforms;
